<template>
  <div>
    <AuthWrapper v-if="isServiceAvailable">
      <a-form
        id="form-login"
        layout="vertical"
        class="login-form"
        :form="formLogin"
        autocomplete="off"
        @submit="onLogin"
      >
        <div style="height: 45px;">&nbsp;</div>
        <div
          class="error-alert"
        >
          <a-alert
              v-if="isInvalidCredentials"
            message="Your authentication information is incorrect. Please try again."
            type="error"
              closable
          />
        </div>
        <div style="height: 30px;">&nbsp;</div>
        <a-form-item v-show="isOTP" label="OTP">
          <a-input
            v-decorator="[
              'otp',
              {
                rules: [
                  {
                    required: isOTP,
                    message: 'Please input OTP',
                  },
                ],
              },
            ]"
            postfix
            placeholder="OTP"
            autocomplete="off"
          />
        </a-form-item>

        <div v-show="!isOTP">
          <a-form-item label="Email" has-feedback>
            <a-input
              v-decorator="[
                'username',
                {
                  rules: [
                    {
                      required: true,
                      whitespace: true,
                      message: 'Please input your Email!',
                    },
                    {
                      message: 'Email invalid!',
                      type: 'email',
                    },
                  ],
                },
              ]"
              postfix
              placeholder="Email"
              autocomplete="off"
            />
          </a-form-item>

          <a-form-item label="Password" class="password">
            <a-input-password
              v-decorator="[
                'password',
                {
                  rules: [
                    { required: true, message: 'Please input your Password!' },
                  ],
                },
              ]"
              prefix
              type="password"
              placeholder="Password"
              autocomplete="off"
            >
              <a-icon slot="prefix" type="lock" class="password-eye" />
            </a-input-password>
          </a-form-item>

          <a-form-item>
            <div class="login-option">
              <a-checkbox
                v-decorator="[
                  'remember',
                  {
                    valuePropName: 'checked',
                    initialValue: false,
                  },
                ]"
                class="remember-button"
              >
                Remember me
              </a-checkbox>
              <a href="javascript:void(0)" @click.prevent="onForgotPassword">
                Forgot password?
              </a>
            </div>
          </a-form-item>
        </div>

        <a-form-item class="mb-0">
          <a-button
            :disabled="isFormInvalid()"
            type="primary"
            html-type="submit"
            size="large"
            class="btn-form"
            :loading="isLoading"
          >
            Sign In
          </a-button>
          <a
            v-if="isOTP"
            href="javascript:void(0);"
            class="login-again-action"
            @click.prevent="loginAgain()"
          >
            Back to sign in
          </a>
        </a-form-item>
      </a-form>
    </AuthWrapper>
    <div v-if="!isServiceAvailable" class="center-screen">
      <img src="@assets/img/logo-large.png" alt="Web Orion Logo" class="logo" />
        <h1>Service unavailable</h1>
        <h2>Please try again later</h2>
    </div>
  </div>
</template>

<script>
import AuthWrapper from './AuthWrapper';
import AuthenticationService from '../services/authentication.service';
import JwtService from '@shared/services/jwt.service';

export default {
  name: 'SignIn',
  components: {
    AuthWrapper,
  },
  data() {
    return {
      isOTP: false,
      isInvalidCredentials: false,
      isLoading: false,
      isServiceAvailable: true,
    };
  },
  async beforeCreate() {
    this.formLogin = this.$form.createForm(this, { name: 'normal_login' });
    this.formOTP = this.$form.createForm(this, { name: 'normal_OTP' });

    try {
      const response = await AuthenticationService.checkService();
      this.isServiceAvailable = response.service_available;
    } catch (e) {
      console.log(e);
      this.isServiceAvailable = false;
    }
  },
  methods: {
    onForgotPassword() {
      const email = this.formLogin.getFieldValue('username') || '';
      const isEmailInValid = this.formLogin.getFieldError('username');
      const query = isEmailInValid ? {} : { email: encodeURIComponent(email) };
      this.$router.push({
        query,
        path: 'forgot-password',
      });
    },
    onLogin(e) {
      e.preventDefault();
      this.formLogin.validateFields(async (err, formValue) => {
        if (!err) {
          const { otp, password, username } = formValue;
          const requestBody = {
            username,
            password,
            otp: this.isOTP && otp ? otp : '',
          };
          this.isInvalidCredentials = false;
          try {
            this.isLoading = true;
            const loginResponse = await AuthenticationService.login(
              requestBody,
            );
            if (loginResponse) {
              this.formLogin.resetFields();
              await this.handleAfterLogin(loginResponse);
            }
          } catch (error) {
            if (error.status === 422 && error.messages && error.messages[0] === "OTP is required") {
              this.isOTP = true;
              this.isInvalidCredentials = false;
            } else {
              this.isInvalidCredentials = true;
              this.formLogin.resetFields();
              this.$showMultiErrorMessage(error.messages || []);
            }
          } finally {
            this.isLoading = false;
          }
        }
      });
    },
    async handleAfterLogin(loginResponse) {
      await this.$store.dispatch('SETTOKEN', loginResponse.token);
      try {
        await this.$store.dispatch('ENSUREUSER');
      } catch (error) {
        await this.$store.dispatch('CLEAR');
        JwtService.destroyToken();
        this.$showErrorMessage('Your account did not load. Try again.');
        return;
      }
      const requested = this.$route.query.redirect;
      const isStaff = this.$store.getters.hasFeature('cloudsine-staff');
      const useAdminHome = isStaff && (!requested || requested === '/' || requested === '/home');
      this.$router.push(useAdminHome ? '/admin/dashboard' : (requested || '/home'));
      this.$showSuccessMessage('Login successful!');
    },

    isFormInvalid() {
      if (!this.formLogin) {
        return true;
      }
      const isOTPValid =
        this.formLogin.getFieldValue('otp') &&
        !this.formLogin.getFieldError('otp');
      if (this.isOTP && !isOTPValid) {
        return true;
      }
      return (
        !this.formLogin.getFieldValue('username') ||
        !this.formLogin.getFieldValue('password') ||
        !!this.formLogin.getFieldError('username') ||
        !!this.formLogin.getFieldError('password')
      );
    },
    loginAgain() {
      this.isOTP = false;
      this.formLogin.resetFields();
    },
  },
};
</script>

<style lang="scss" scoped>
.login-option {
  display: flex;
  justify-content: space-between;
}
.btn-form {
  display: block;
  width: 100%;
  margin-top: 27px;
}
.error-alert.transparent {
  opacity: 0;
}
.login-again-action {
  margin-top: 10px;
  display: block;
  text-align: center;
}
.center-screen {
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  text-align: center;
  height: 100vh;
  font-size: 25px;
  padding-bottom: 50px;
}
.logo {
  display: inline;
  margin: 0 auto;
  width: 30%;
}
</style>
